import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

/**
 * Measured content height for a `max-height` collapse/expand section.
 *
 * The Design page's five sections used to animate against hard-coded pixel
 * budgets — `max-h-[1500px]`, `max-h-[800px]`, and two computed guesses like
 * `(answers + questions) * 250px` — on containers with `overflow-hidden`.
 * Any content taller than its guess was silently cut off. Measured live on
 * staging: a screening question sat at scrollHeight 1109px inside a 1000px
 * budget before the researcher touched anything, and adding one answer left
 * the "Add new answer" button's top edge at 999px — 1px of a 40px control
 * inside the clip window (TELV1-572).
 *
 * Attach the returned ref to the CONTENT element *inside* the animated
 * wrapper, never to the wrapper whose max-height you are setting — observing
 * an element whose size you also drive is how you get "ResizeObserver loop
 * completed with undelivered notifications" into Sentry.
 *
 * Measuring works while the section is closed too: `max-height: 0` on the
 * wrapper clips the child, it does not compress it, so the child keeps its
 * natural height and the close animation still has a real starting point.
 *
 * Returns a CALLBACK ref (not a RefObject) because these sections mount
 * conditionally behind loading skeletons — the measurement has to re-arm
 * whenever the element actually appears. Mirrors useBottomAwareStickyTop.
 *
 * Height 0 means "not measured yet" (first paint, or an environment without
 * ResizeObserver such as jsdom). Callers should treat that as `none` rather
 * than `0px`: a failed measurement must fail OPEN, never clipped.
 */
export const useMeasuredHeight = () => {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState(0);

  const ref = useCallback((node: HTMLElement | null) => setEl(node), []);

  // Re-measure after EVERY commit, deliberately without a dependency array.
  // Adding a screener answer or an interview question is a React state
  // change, so this catches it even where ResizeObserver never fires — which
  // is not hypothetical: in a backgrounded/hidden document the observer does
  // not run at all (verified: a fresh observer on a grown element produced no
  // callback while document.hidden was true), and without this the section
  // would keep animating to a stale height. The observer below still earns
  // its keep for changes React does not drive: web fonts, images, a textarea
  // growing as the user types.
  //
  // The rule's "infinite chain of updates" warning does not apply: setHeight
  // with an unchanged number is a no-op for React, so the chain terminates on
  // the first commit where the measurement is stable. Adding [el] as the rule
  // suggests would defeat the point — the element identity does not change
  // when its content grows, which is the case this exists to catch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (el) setHeight(el.offsetHeight);
  });

  useEffect(() => {
    // jsdom has no ResizeObserver — same guard as useBottomAwareStickyTop.
    // Callers fall back to an unbounded max-height, so tests render the
    // section fully expanded rather than collapsed to nothing.
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => setHeight(el.offsetHeight);

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    // Late-layout insurance, as in useBottomAwareStickyTop: fonts, images and
    // lazily-rendered question rows land after mount. Duplicate measurements
    // set the same value, which React bails out on.
    const raf = requestAnimationFrame(update);
    const settle = setTimeout(update, 300);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(settle);
    };
  }, [el]);

  return [ref, height] as const;
};

/**
 * The inline style for an animated section wrapper. Kept next to the hook so
 * the "0 means unmeasured, so don't clip" rule lives in one place.
 */
export const collapsibleMaxHeight = (isOpen: boolean, measured: number) => {
  if (!isOpen) return 0;
  return measured > 0 ? `${measured}px` : 'none';
};

export default useMeasuredHeight;
